import { useEffect, useState } from "react";
import { Trophy, Flame, Crown, Medal, Award, Phone, Mail } from "lucide-react";
import { api } from "../../lib/api";

const BADGE_ICONS = { medal: Medal, trophy: Trophy, crown: Crown, flame: Flame };
const TIER_COLORS = {
  bronze:   "text-[#e3a86e]",
  silver:   "text-[#d7dce5]",
  gold:     "text-[#d4af37]",
  platinum: "text-[#fdfbf7]",
};
const RANK_MEDAL = {
  1: { color: "bg-[#d4af37] text-[#041f1b]", label: "🥇" },
  2: { color: "bg-[#c0c7d4] text-[#041f1b]", label: "🥈" },
  3: { color: "bg-[#b08d57] text-[#041f1b]", label: "🥉" },
};

/**
 * AdminLeaderboard — ranking of employees. Toggles between current month and
 * all-time views, highlights top 3 with medals, shows each employee's current
 * streak + top unlocked badge.
 */
export default function AdminLeaderboard() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/employee-leaderboard", { params: { period } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [period]);

  const items = data?.items || [];
  const podium = items.slice(0, 3);
  const rest = items.slice(3);

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-3">Desempenho</div>
        <h1 className="font-display text-5xl">Ranking de funcionários.</h1>
        <div className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
          Quem está a liderar as limpezas concluídas. Use isto para reconhecer o top performer e identificar quem precisa de suporte.
        </div>
      </div>

      {/* Period toggle */}
      <div className="flex gap-2" data-testid="leaderboard-period-toggle">
        {[
          { key: "month", label: "Este mês" },
          { key: "all", label: "Desde sempre" },
        ].map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm ${period === p.key ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]" : "border-[#1a4640] text-[#a0b2af]"}`}
                  data-testid={`period-${p.key}`}>
            {p.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-[#a0b2af] text-sm">A carregar ranking…</div>}

      {!loading && items.length === 0 && (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]">
          Sem funcionários aprovados.
        </div>
      )}

      {/* Podium — top 3 */}
      {!loading && podium.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-4" data-testid="podium">
          {podium.map(p => {
            const medal = RANK_MEDAL[p.rank];
            const topBadge = p.top_badge;
            const Ic = topBadge ? (BADGE_ICONS[topBadge.icon] || Award) : null;
            return (
              <div key={p.employee_id}
                   className={`lux-card p-5 border-2 ${p.rank === 1 ? "border-[#d4af37] shadow-[0_0_30px_rgba(212,175,55,0.15)]" : "border-[#1a4640]"}`}
                   data-testid={`podium-rank-${p.rank}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-display text-xl ${medal?.color || "bg-[#1a4640]"}`}>
                    #{p.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl truncate">{p.name}</div>
                    <div className="text-[11px] text-[#a0b2af] truncate">{p.email}</div>
                  </div>
                  {Ic && <Ic size={22} className={TIER_COLORS[topBadge.tier]} />}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="font-display text-2xl text-[#d4af37]">{p.period_completed}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#a0b2af] mt-0.5">No período</div>
                  </div>
                  <div>
                    <div className="font-display text-2xl">{p.total_completed}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#a0b2af] mt-0.5">Total</div>
                  </div>
                  <div>
                    <div className="font-display text-2xl text-[#f59e0b] flex items-center justify-center gap-1">
                      <Flame size={14} /> {p.streak}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-[#a0b2af] mt-0.5">Streak</div>
                  </div>
                </div>
                {p.badges_count > 0 && (
                  <div className="mt-3 text-[10px] text-[#d4af37] uppercase tracking-widest text-center">
                    {p.badges_count} distintivo{p.badges_count > 1 ? "s" : ""} desbloqueado{p.badges_count > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Rest of ranking — compact rows */}
      {rest.length > 0 && (
        <div className="lux-card overflow-hidden" data-testid="leaderboard-rest">
          <div className="divide-y divide-[#1a4640]">
            {rest.map(r => (
              <div key={r.employee_id} className="p-4 flex items-center gap-3 hover:bg-[#0a3a33]/30"
                   data-testid={`leaderboard-row-${r.rank}`}>
                <div className="w-10 h-10 rounded-full bg-[#1a4640] text-[#a0b2af] font-display text-sm grid place-items-center flex-shrink-0">
                  #{r.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{r.name}</div>
                  <div className="text-[11px] text-[#a0b2af] flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Mail size={10} /> {r.email}</span>
                    {r.phone && <span className="flex items-center gap-1"><Phone size={10} /> {r.phone}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-5 text-right flex-shrink-0">
                  <div>
                    <div className="font-display text-xl text-[#d4af37]">{r.period_completed}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#a0b2af]">No período</div>
                  </div>
                  <div className="hidden sm:block">
                    <div className="font-display text-xl">{r.total_completed}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#a0b2af]">Total</div>
                  </div>
                  {r.streak > 0 && (
                    <div className="flex items-center gap-1 text-[#f59e0b] font-mono text-sm">
                      <Flame size={12} /> {r.streak}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
